import { useState, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { ArrowUp, Paperclip, MessageSquare, ListChecks, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { PxMark } from "@/components/PxMark";
import { supabase } from "@/integrations/supabase/client";

interface TemplateItem {
  label: string;
  prompt: string;
}

// Templates business - PME, freelances, artisans, pros
const templates: TemplateItem[] = [
  {
    label: "Site vitrine",
    prompt: "Crée un site vitrine professionnel avec: hero impactant avec proposition de valeur, section services (3-4 cartes), à propos avec photo/équipe, témoignages clients, formulaire de contact, footer avec coordonnées et réseaux. Design moderne et crédible.",
  },
  {
    label: "Artisan",
    prompt: "Crée un site pour artisan avec: hero avec photo du métier et bouton 'Demander un devis', zone d'intervention (carte/liste villes), prestations détaillées avec tarifs indicatifs, galerie de réalisations, avis clients, formulaire de devis rapide. Design professionnel et rassurant.",
  },
  {
    label: "Boutique",
    prompt: "Crée une boutique e-commerce avec: page catalogue avec grille produits et filtres, page produit avec images, description, variantes, bouton panier, page panier avec récapitulatif, page checkout simplifiée. Design épuré type boutique moderne.",
  },
  {
    label: "Restaurant",
    prompt: "Crée un site de restaurant avec: hero avec image appétissante et bouton réserver, menu du jour en évidence, carte complète organisée par catégories avec prix, galerie ambiance, horaires d'ouverture, localisation avec carte, formulaire de réservation. Design chaleureux et gourmand.",
  },
  {
    label: "Landing",
    prompt: "Crée une landing page d'agence avec: hero audacieux avec CTA, section problème/solution, services ou produits phares, logos clients, témoignages, équipe, pricing ou processus de travail, CTA final. Design innovant et dynamique.",
  },
  {
    label: "Portfolio",
    prompt: "Crée un portfolio personnel avec: hero animé avec nom, titre et liens sociaux, section À propos avec photo, grille de projets avec cartes (image, titre, techno), compétences avec barres, parcours/expériences, formulaire de contact. Design créatif et moderne.",
  },
  {
    label: "Dashboard",
    prompt: "Crée un dashboard avec: sidebar navigation, header avec profil/notifications, page overview avec 4-6 KPI cards, graphiques (courbes, barres), tableau de données avec pagination et recherche, formulaire de paramètres. Theme dark professionnel.",
  },
  {
    label: "Prise de RDV",
    prompt: "Crée une app de prise de rendez-vous avec: calendrier interactif, créneaux disponibles, formulaire de réservation (nom, email, service), confirmation par email simulée, page mes rendez-vous. Design simple et fonctionnel type Calendly.",
  },
  {
    label: "Photo studio",
    prompt: "Crée un portfolio photo avec: galerie masonry plein écran, lightbox avec navigation, filtres par catégorie (mariage, portrait, corporate), page À propos avec bio, tarifs/forfaits, formulaire de contact. Design minimaliste mettant en valeur les images.",
  },
];

import pixelsDecoration from "@/assets/pixels-decoration.svg";

// Frise de pixels en bas — l'élément de marque, en sobre
const PixelDecoration = () => {
  return (
    <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none flex opacity-40" style={{ gap: 0, margin: 0 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <img
          key={i}
          src={pixelsDecoration}
          alt=""
          className="flex-shrink-0 block -mr-px"
          style={{ height: 'auto', width: 'auto', display: 'block' }}
        />
      ))}
    </div>
  );
};
interface HomeContentProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  userName?: string;
}
export const HomeContent = ({
  onSendMessage,
  isLoading = false,
  userName
}: HomeContentProps) => {
  const [inputValue, setInputValue] = useState("");
  const [chatMode, setChatMode] = useState<'chat' | 'plan'>('chat');
  const [selectedImage, setSelectedImage] = useState<{ file: File; preview: string; base64: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle image selection
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image trop lourde (max 5 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setSelectedImage({
        file,
        preview: URL.createObjectURL(file),
        base64: base64.split(',')[1]
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  // Remove selected image
  const removeImage = useCallback(() => {
    if (selectedImage?.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }
    setSelectedImage(null);
  }, [selectedImage]);

  const [isUploading, setIsUploading] = useState(false);

  // Upload image to Supabase Storage
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat-images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        // Fallback: if bucket doesn't exist, we might need to handle it or show error
        // specific error for "bucket not found"?
        if (uploadError.message.includes('Bucket not found')) {
          alert("Le bucket 'chat-images' n'existe pas sur Supabase. Veuillez le créer.");
        }
        return null;
      }

      const { data } = supabase.storage
        .from('chat-images')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = useCallback(async () => {
    if ((inputValue.trim() || selectedImage) && !isLoading && !isUploading) {

      let finalMessage = inputValue.trim();
      let imageUrl = null;

      if (selectedImage) {
        // Upload to Supabase
        imageUrl = await uploadImage(selectedImage.file);

        if (!imageUrl) {
          // Fallback to base64 if upload fails? Or just stop?
          // User specifically asked for asset so let's try to enforce it.
          // If failed, maybe alert user?
          alert("Échec de l'upload de l'image. Vérifiez votre connexion Supabase.");
          return;
        }
      }

      let messageContent = chatMode === 'plan'
        ? `[PLAN_MODE] ${finalMessage}`
        : finalMessage;

      if (imageUrl) {
        // Send as Asset URL
        messageContent = `[IMAGE_ASSET:${imageUrl}] ${messageContent || 'Analyse cette maquette et reproduis-la'}`;
      }

      onSendMessage(messageContent);
      setInputValue("");
      removeImage();
    }
  }, [inputValue, isLoading, isUploading, onSendMessage, chatMode, selectedImage, removeImage]);
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);
  const handleSuggestionClick = useCallback((prompt: string) => {
    onSendMessage(prompt);
  }, [onSendMessage]);
  return <div className="flex-1 h-full flex flex-col overflow-hidden relative bg-background">
    {/* Colonne éditoriale */}
    <div className="relative z-10 flex-1 overflow-y-auto">
      <div className="min-h-full w-full max-w-[1040px] mx-auto px-8 pt-16 pb-48 flex flex-col justify-center">
        {/* Salutation — 1 des 3 usages Instrument Serif */}
        <div className="mb-10 max-w-[760px]">
          <h1 className="font-display italic text-[44px] leading-[1.05] text-foreground">
            {userName ? `Bonsoir, ${userName}.` : "Bonsoir."}
          </h1>
          <p className="mt-3 font-mono text-[11px] text-faint">
            Décris ton app, Pixel la construit.
          </p>
        </div>

        {/* Le héros : le prompt */}
        <div className="w-full max-w-[760px] mb-6 bg-card border border-hairline-strong">
          <div className="flex items-stretch">
            <Textarea
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Décris ton app…"
              className="flex-1 min-h-14 px-4 py-4 bg-transparent border-none outline-none resize-none text-[15px] text-foreground placeholder:text-faint focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={isLoading}
            />
            <button
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Générer"
              className={cn(
                "w-14 shrink-0 flex items-center justify-center transition-colors",
                isLoading || isUploading
                  ? "bg-secondary cursor-wait"
                  : "bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              )}
            >
              {isLoading || isUploading
                ? <PxMark state="generating" size={16} label="Génération en cours" />
                : <ArrowUp className="w-4 h-4" />}
            </button>
          </div>

          {/* Préview de la maquette jointe */}
          {selectedImage && (
            <div className="px-4 py-3 border-t border-border">
              <div className="relative inline-block">
                <img
                  src={selectedImage.preview}
                  alt="Maquette"
                  className="h-20 object-cover border border-border"
                />
                <button
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 p-1 bg-foreground text-background transition-opacity hover:opacity-80"
                  aria-label="Retirer la maquette"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <p className="font-mono text-[11px] text-faint mt-2">Maquette jointe — Pixel la reproduira</p>
            </div>
          )}

          {/* Pied du composer */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-border">
            <div className="flex items-center gap-2">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="Joindre une maquette"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              {/* Bascule Chat/Plan */}
              <div className="flex items-center border border-border">
                <button
                  type="button"
                  onClick={() => setChatMode('chat')}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors",
                    chatMode === 'chat'
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title="Mode chat — exécution directe"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  Chat
                </button>
                <button
                  type="button"
                  onClick={() => setChatMode('plan')}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors",
                    chatMode === 'plan'
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  title="Mode plan — proposition avant exécution"
                >
                  <ListChecks className="w-3.5 h-3.5" />
                  Plan
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Chips suggestions */}
        <div className="w-full max-w-[760px] flex flex-wrap gap-2">
          {templates.map((template) => (
            <button
              key={template.label}
              onClick={() => handleSuggestionClick(template.prompt)}
              disabled={isLoading}
              className="px-3 py-1.5 border border-border font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {template.label}
            </button>
          ))}
        </div>
      </div>
    </div>

    {/* Frise pixel en bas */}
    <PixelDecoration />
  </div>;
};
